import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scissors, LogIn, ShoppingBag, Soup, Stethoscope, ArrowRight, KeyRound } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { AnimatedAfrica } from '@/components/AnimatedAfrica';
import { HeroHeadline } from '@/components/HeroHeadline';
import { LiveCounters } from '@/components/LiveCounters';
import { MobileMoneyShowcase } from '@/components/MobileMoneyShowcase';
import { TsenaBadge } from '@/components/TsenaBadge';
import { makeT, SUPPORTED_LANGS, type Lang } from '@/lib/i18n';
import type { RegionCode } from '@nako/core';

const LANG_DISPLAY: Record<Lang, { flag: string; label: string }> = {
  en: { flag: '🇬🇧', label: 'English' },
  tn: { flag: '🇧🇼', label: 'Setswana' },
  zu: { flag: '🇿🇦', label: 'isiZulu' },
  ha: { flag: '🇳🇬', label: 'Hausa' },
  pcm: { flag: '🇳🇬', label: 'Pidgin' },
  sn: { flag: '🇿🇼', label: 'Shona' },
};

interface DemoBiz {
  email: string;
  password: 'admin';
  name: string;
  blurb: string;
  icon: typeof Scissors;
  bgClass: string;
}

const DEMOS: DemoBiz[] = [
  {
    email: 'ritahbeauty@nakopula.demo',
    password: 'admin',
    name: 'Ritah Beauty Parlour',
    blurb:
      'Hair, nails, lashes, beauty products. Walk-ins and appointments, loyalty cards, monthly stylist commissions.',
    icon: Scissors,
    bgClass: 'from-[#9D174D] to-[#4C1D95]',
  },
  {
    email: 'paxdental@nakopula.demo',
    password: 'admin',
    name: 'Pako Dental Studio',
    blurb:
      'Tooth charts, prescriptions, patient recall, clinical notes, and automated tax-ready accounting.',
    icon: Stethoscope,
    bgClass: 'from-[#0E7490] to-[#0A0E27]',
  },
  {
    email: 'lunarosa@nakopula.demo',
    password: 'admin',
    name: 'Luna Rosa Restaurant',
    blurb: 'Food cost, table revenue, beverage sales, kitchen wages, tax-ready.',
    icon: Soup,
    bgClass: 'from-[#9A3412] to-[#0A0E27]',
  },
  {
    email: 'tsenafashion@nakopula.demo',
    password: 'admin',
    name: 'Tsena Fashion & Fragrance',
    blurb:
      'Perfumes, clothing, shoes. Track stock, record sales, see your profit — built for Botswana retailers.',
    icon: ShoppingBag,
    bgClass: 'from-[#7C3AED] to-[#1E1B4B]',
  },
];

export default function Welcome() {
  const session = useAuthStore((s) => s.session);
  const profile = useAuthStore((s) => s.profile);
  const business = useAuthStore((s) => s.business);
  const loadProfileAndBusiness = useAuthStore((s) => s.loadProfileAndBusiness);
  const lang = useUiStore((s) => s.lang);
  const setLang = useUiStore((s) => s.setLang);
  const mode = useUiStore((s) => s.mode);
  const setMode = useUiStore((s) => s.setMode);
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const hasChosenRegion = useRegionStore((s) => s.hasChosen);
  const setRegion = useRegionStore((s) => s.setRegion);
  const toast = useToast();
  const navigate = useNavigate();
  const t = makeT(lang);

  // Auto-cycle the header language every 5s so every market sees its own
  // tongue while the page sits idle. Stops once the user clicks somewhere
  // that explicitly sets a language (i.e. the sidebar toggle inside the
  // app). Local pause state lets a click on the header button hold the
  // current language for one round.
  const [pauseLangLoop, setPauseLangLoop] = useState(false);
  useEffect(() => {
    if (pauseLangLoop) return;
    const id = window.setInterval(() => {
      const i = SUPPORTED_LANGS.indexOf(lang);
      const next = SUPPORTED_LANGS[(i + 1) % SUPPORTED_LANGS.length]!;
      setLang(next);
    }, 5000);
    return () => window.clearInterval(id);
  }, [lang, setLang, pauseLangLoop]);
  const advanceLang = () => {
    const i = SUPPORTED_LANGS.indexOf(lang);
    const next = SUPPORTED_LANGS[(i + 1) % SUPPORTED_LANGS.length]!;
    setLang(next);
    // Click resets the loop window so the manually-picked lang gets a
    // fresh 5s before the next auto-step.
    setPauseLangLoop(true);
    window.setTimeout(() => setPauseLangLoop(false), 50);
  };

  const onPickRegion = (code: RegionCode) => {
    setRegion(code);
    // Stop the auto-cycle and reset to English so the app UI is readable
    setPauseLangLoop(true);
    setLang('en');
    const regionKey = ('region.' + code) as 'region.BW' | 'region.ZA' | 'region.NG' | 'region.ZW';
    toast.show(`${t('landing.toast_welcome')} ${t(regionKey)} ${t('landing.toast_mode')}`, {
      tone: 'success',
    });
  };

  // Fun Mode easter egg: 5 taps on the logo within 1.5 s flips a playful
  // theme. Tapping again exits back to whatever the previous mode was.
  const [logoTapTimes, setLogoTapTimes] = useState<number[]>([]);
  const onLogoTap = () => {
    if (mode === 'fun') {
      setMode('normal');
      toast.show('Fun Mode off — back to business 💼', { tone: 'success' });
      return;
    }
    const now = Date.now();
    const recent = [...logoTapTimes.filter((t) => now - t < 1500), now];
    setLogoTapTimes(recent);
    if (recent.length >= 5) {
      setLogoTapTimes([]);
      setMode('fun');
      toast.show('🎉 Fun Mode activated! Calculations still accurate, promise.', {
        tone: 'success',
      });
    }
  };

  // Real (non-admin) customers shouldn't see the demo cards on the homepage —
  // it's confusing once they've signed up for their own business.
  const showDemos = !session || (profile?.is_admin ?? false);

  // Password-modal flow: clicking a demo card opens a sign-in dialog
  // pre-filled with the demo password. Visual confirmation that the user is
  // signing in to the right business, and forces a fresh code path.
  const [pending, setPending] = useState<DemoBiz | null>(null);
  const [pwd, setPwd] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function openLogin(d: DemoBiz) {
    setPending(d);
    setPwd('admin');
    setError(null);
  }

  function closeLogin() {
    setPending(null);
    setPwd('admin');
    setError(null);
  }

  async function submitLogin() {
    if (!pending) return;
    setBusy(true);
    setError(null);

    if (session) await supabase.auth.signOut();

    const { error: err } = await supabase.auth.signInWithPassword({
      email: pending.email,
      password: pwd,
    });
    if (err) {
      setError(err.message);
      setBusy(false);
      return;
    }
    const { data } = await supabase.auth.getSession();
    if (data.session) await loadProfileAndBusiness(data.session.user.id);
    setBusy(false);
    closeLogin();
    // Ensure language is English when entering the app
    setLang('en');
    toast.show(`Welcome to ${pending.name}`, { tone: 'success' });
    navigate('/app');
  }

  return (
    <main className="bg-paper min-h-screen">
      {!session && <TsenaBadge />}

      {/* Top — pad the right side so the Tsena badge doesn't collide on small phones */}
      <header className="flex flex-wrap items-start justify-between gap-y-2 px-4 py-4 pr-24 sm:pr-32 md:px-8 md:pr-44">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onLogoTap}
            className="font-display cursor-pointer text-5xl font-bold italic leading-none tracking-tight text-[#0a0e27] drop-shadow-[0_2px_0_rgba(217,119,6,0.25)] sm:text-6xl md:text-7xl"
            aria-label="Nako Pula"
            title={mode === 'fun' ? 'Tap to exit Fun Mode' : undefined}
          >
            Nako <span className="text-accent not-italic">Pula</span>
          </button>
          <Pill tone="accent">{mode === 'fun' ? '🎉 Fun' : t('landing.beta')}</Pill>
        </div>
        <div className="flex max-w-full items-center gap-2 sm:gap-3">
          {session && (
            <Button size="sm" onClick={() => navigate('/app')} leftIcon={<LogIn size={14} />}>
              <span className="max-w-[8rem] truncate sm:max-w-[10rem] md:max-w-none">
                {business ? `${t('landing.continue_to')} ${business.name}` : t('landing.continue')}
              </span>
            </Button>
          )}
          <button
            type="button"
            onClick={advanceLang}
            className="text-muted hover:text-ink inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums sm:text-base md:gap-2 md:text-2xl"
            title="Auto-cycles every 5s — click to skip"
            aria-label={`Language: ${LANG_DISPLAY[lang].label}. Click to switch.`}
          >
            <span aria-hidden="true" className="text-base sm:text-lg md:text-3xl">
              {LANG_DISPLAY[lang].flag}
            </span>
            <span>{LANG_DISPLAY[lang].label}</span>
          </button>
        </div>
      </header>

      {/* Hero — editorial-futurism upgrade */}
      <section className="mx-auto max-w-5xl px-4 pb-8 pt-2 text-center md:px-8 md:pb-12 md:pt-4">
        <HeroHeadline>
          {(() => {
            if (lang === 'tn')
              return 'Akhaonting ya di-SME tsa Botswana e e sa duelelweng P 8,400.';
            if (hasChosenRegion && regionCode === 'ZA')
              return "Accounting for South African SMEs that doesn't cost R 8,400.";
            if (hasChosenRegion && regionCode === 'NG')
              return "Accounting for Nigerian SMEs that doesn't cost ₦ 1,000,000.";
            if (hasChosenRegion && regionCode === 'ZW')
              return "Accounting for Zimbabwean SMEs that doesn't cost US$ 600.";
            return "Accounting for African SMEs that doesn't break the bank.";
          })()}
        </HeroHeadline>

        <div className="mt-12 md:mt-16">
          <AnimatedAfrica onSelect={onPickRegion} selected={hasChosenRegion ? regionCode : null} />
        </div>

        <p className="text-muted mx-auto mt-6 max-w-2xl text-base md:text-lg">
          {hasChosenRegion
            ? `${t('landing.region_mode_prefix')} ${t(('region.' + regionCode) as 'region.BW')} ${t('landing.region_mode_suffix')} ${regionConfig.blurb}`
            : t('landing.pick_country')}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Pill tone="green">{t('landing.pill_pwa')}</Pill>
          <Pill tone="blue">{t('landing.pill_multiregion')}</Pill>
          <Pill tone="accent">{t('landing.pill_ocr')}</Pill>
          <Pill tone="ink">{regionConfig.vat.returnForm}</Pill>
        </div>
      </section>

      <LiveCounters />

      {/* Mobile-money showcase — display only. Actual collection rail (RealPay
          Collect Mobile) is in development. Filtered to the user's region
          once they've picked one on the Africa map; otherwise shows the full
          BW + ZA + NG roster. */}
      <section className="mx-auto max-w-5xl px-4 md:px-8">
        <MobileMoneyShowcase region={hasChosenRegion ? regionCode : null} />
      </section>

      {/* Demo accounts — hidden for real customers once they're signed in */}
      {showDemos && (
        <section className="mx-auto max-w-5xl px-4 pb-10 md:px-8 md:pb-16">
          <div className="mb-3">
            <h2 className="font-display text-2xl">{t('landing.try_demo')}</h2>
            <p className="text-muted text-xs">
              <KeyRound size={12} className="mr-1 inline" />
              {t('landing.demo_note')}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {DEMOS.map((d) => {
              const Icon = d.icon;
              return (
                <button key={d.email} onClick={() => openLogin(d)} className="group text-left">
                  <Card
                    className={`bg-gradient-to-br ${d.bgClass} text-paper border-transparent transition-transform group-hover:-translate-y-0.5`}
                  >
                    <Icon size={32} className="mb-3 opacity-90" />
                    <h3 className="font-display text-xl font-semibold">{d.name}</h3>
                    <p className="text-paper-2 mb-4 mt-1 min-h-[3rem] text-sm">{d.blurb}</p>
                    <div className="flex items-center gap-1 text-sm font-medium">
                      Log in to {d.name.split(' ')[0]} <ArrowRight size={14} />
                    </div>
                  </Card>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Demo password modal */}
      <Modal
        open={pending !== null}
        onClose={closeLogin}
        title={pending ? `Log in to ${pending.name}` : ''}
      >
        {pending && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitLogin();
            }}
            className="space-y-4"
          >
            <p className="text-muted text-sm">
              Demo account · <span className="font-mono">{pending.email}</span>
            </p>
            <Input
              label="Password"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoFocus
              hint="The demo password is admin"
              error={error ?? undefined}
            />
            <Button type="submit" full loading={busy} leftIcon={<LogIn size={16} />}>
              {t('landing.signin')}
            </Button>
            <button
              type="button"
              onClick={closeLogin}
              className="text-muted hover:text-ink mx-auto block text-xs"
            >
              Cancel
            </button>
          </form>
        )}
      </Modal>

      {/* Real signup */}
      <section className="mx-auto max-w-3xl px-4 pb-12 md:px-8">
        <Card className="text-center">
          <h2 className="font-display mb-2 text-xl">{t('landing.got_business')}</h2>
          <p className="text-muted mb-4 text-sm">
            {t('landing.signup_copy_pre')}{' '}
            {(() => {
              if (hasChosenRegion && regionCode === 'ZA') return 'R 150/month.';
              if (hasChosenRegion && regionCode === 'NG') return '₦ 5,000/month.';
              if (hasChosenRegion && regionCode === 'ZW') return 'US$ 10/month.';
              return 'P 100/month.';
            })()}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" onClick={() => navigate('/login')} full>
              {t('landing.signin')}
            </Button>
            <Button onClick={() => navigate('/login?signup=1')} full>
              {t('landing.create_account')}
            </Button>
          </div>
        </Card>
      </section>

      <footer className="border-line text-muted border-t px-4 py-6 text-center text-xs">
        © {new Date().getFullYear()} Nako Tech Pty Ltd · Gaborone · {t('landing.footer_copy')}
      </footer>
    </main>
  );
}
